import jsPDF from 'jspdf';
import { Inspection } from './inspectionService';
import { FormTemplate } from '@easydone/shared';

interface ReportData {
  inspection: Inspection;
  template: FormTemplate;
  orgName: string;
  orgLogo?: string;
}

/**
 * Generate a PDF report for an inspection
 */
export async function generateInspectionReport(data: ReportData): Promise<Blob> {
  const { inspection, template, orgName } = data;

  // Create PDF document (A4 size)
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = margin;

  // Helper function to add new page if needed
  const checkPageBreak = (requiredSpace: number) => {
    if (yPos + requiredSpace > pageHeight - margin) {
      pdf.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // Header - Organization Logo and Name
  pdf.setFillColor(59, 130, 246); // Primary blue
  pdf.rect(0, 0, pageWidth, 40, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text(orgName, margin, 20);

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Inspection Report', margin, 30);

  yPos = 50;

  // Inspection Details Section
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Inspection Details', margin, yPos);
  yPos += 8;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');

  const formatTimestamp = (ts: any) => {
    if (!ts) return 'N/A';
    if (typeof ts === 'number') return new Date(ts).toLocaleDateString();
    if (ts.toDate) return new Date(ts.toDate()).toLocaleDateString();
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString();
    return 'N/A';
  };

  const details = [
    ['Template:', template.name],
    ['Site:', inspection.siteName],
    ...(inspection.flocName ? [['FLOC:', inspection.flocName]] : []),
    ...(inspection.assetName ? [['Asset:', inspection.assetName]] : []),
    ['Inspector:', inspection.inspectorName || 'N/A'],
    ['Date:', formatTimestamp(inspection.completedAt)],
    ['Status:', inspection.status.toUpperCase()],
  ];

  details.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label, margin, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value || '', margin + 40, yPos);
    yPos += 6;
  });

  yPos += 5;

  // Template Description
  if (template.description) {
    checkPageBreak(20);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Description:', margin, yPos);
    yPos += 6;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const descLines = pdf.splitTextToSize(template.description, contentWidth);
    pdf.text(descLines, margin, yPos);
    yPos += descLines.length * 5 + 5;
  }

  // Sections and Fields
  for (const section of template.sections) {
    checkPageBreak(30);

    // Section Header
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, yPos - 5, contentWidth, 10, 'F');

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(section.title, margin + 2, yPos);
    yPos += 10;

    // Section Description
    if (section.description) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(100, 100, 100);
      const sectionDescLines = pdf.splitTextToSize(section.description, contentWidth);
      pdf.text(sectionDescLines, margin, yPos);
      yPos += sectionDescLines.length * 4 + 3;
    }

    // Fields
    const sectionData = inspection.formData.sections.find(s => s.sectionId === section.id);

    for (const field of section.fields) {
      checkPageBreak(20);

      const fieldData = sectionData?.fields.find(f => f.fieldId === field.id);
      const value = fieldData?.value;

      // Field Label
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(`${field.label}${field.required ? ' *' : ''}:`, margin, yPos);
      yPos += 5;

      // Field Value
      pdf.setFont('helvetica', 'normal');

      if (Array.isArray(value)) {
        // Handle photo arrays or checkbox arrays
        if (value.length > 0 && typeof value[0] === 'string' && value[0].startsWith('http')) {
          // Photos - show count and note
          pdf.setTextColor(59, 130, 246);
          pdf.text(`${value.length} photo(s) attached`, margin + 5, yPos);
          pdf.setTextColor(0, 0, 0);
          yPos += 5;
        } else {
          // Checkbox values
          pdf.text(value.join(', '), margin + 5, yPos);
          yPos += 5;
        }
      } else if (typeof value === 'string' && value.startsWith('http')) {
        // Signature or photo URL
        pdf.setTextColor(59, 130, 246);
        pdf.text('Signature/Photo attached', margin + 5, yPos);
        pdf.setTextColor(0, 0, 0);
        yPos += 5;
      } else {
        // Regular value
        const displayValue = value !== null && value !== undefined && value !== ''
          ? String(value)
          : 'N/A';
        const valueLines = pdf.splitTextToSize(displayValue, contentWidth - 5);
        pdf.text(valueLines, margin + 5, yPos);
        yPos += valueLines.length * 5;
      }

      yPos += 3;
    }

    yPos += 5;
  }

  // Review Notes (if reviewed)
  if (inspection.reviewNotes) {
    checkPageBreak(30);

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Review Notes', margin, yPos);
    yPos += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const notesLines = pdf.splitTextToSize(inspection.reviewNotes, contentWidth);
    pdf.text(notesLines, margin, yPos);
    yPos += notesLines.length * 5 + 5;
  }

  // Footer on last page
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
      `Generated on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    pdf.text(
      'Generated with EasyDone-Pro',
      pageWidth - margin,
      pageHeight - 10,
      { align: 'right' }
    );
  }

  return pdf.output('blob');
}

/**
 * Download inspection report as PDF
 */
export async function downloadInspectionReport(data: ReportData): Promise<void> {
  const blob = await generateInspectionReport(data);

  // Create filename
  const date = new Date().toISOString().split('T')[0];
  const filename = `Inspection_${data.inspection.assetName}_${date}.pdf`;

  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  // Cleanup
  URL.revokeObjectURL(url);
}

/**
 * Generate and upload inspection report to Firebase Storage
 */
export async function uploadInspectionReport(
  data: ReportData,
  orgId: string,
  inspectionId: string
): Promise<string> {
  const blob = await generateInspectionReport(data);

  // Import storage functions dynamically to avoid circular dependencies
  const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
  const { storage } = await import('../config/firebase');

  const timestamp = Date.now();
  const filename = `inspection-report-${timestamp}.pdf`;
  const storagePath = `organizations/${orgId}/reports/${inspectionId}/${filename}`;

  const storageRef = ref(storage, storagePath);
  const snapshot = await uploadBytes(storageRef, blob, {
    contentType: 'application/pdf',
  });

  const downloadURL = await getDownloadURL(snapshot.ref);
  return downloadURL;
}
